/**
 * Canvas drag-and-drop sensors and accessibility (spec section 9).
 *
 * The canvas builder previously registered only a PointerSensor, so an element
 * could be moved with a mouse or touch but not with a keyboard, and a screen
 * reader received no drag announcements. Spec section 9 requires keyboard-
 * operable drag-and-drop with ARIA announcements. This module adds a
 * KeyboardSensor alongside the existing PointerSensor and supplies the
 * announcement and instruction strings for the DndContext `accessibility` prop.
 *
 * KEYBOARD-GETTER NOTE (slice note to plan): the canvas uses CUSTOM collision
 * detection; the grid cells are not registered as dnd-kit droppables (spec/plan
 * ruling: keep the custom collision, do NOT migrate GridDropZone to
 * useDroppable). dnd-kit's stock keyboard coordinate getters
 * (sortableKeyboardCoordinates, closestCorners snapping) depend on registered
 * droppable rects, which are absent here, so they cannot be used. Instead this
 * module supplies a custom getter that translates the dragged element by exactly
 * one grid cell pitch per arrow-key press. The pitch (one column + gap
 * horizontally, one row + gap vertically, in px) is provided by the component,
 * which knows the runtime grid geometry; the parent's existing custom collision
 * then resolves the resulting pointer position to a target cell, exactly as it
 * does for pointer drags. Consequence: the drag announcements below report pick
 * up / move / drop / cancel keyed on the active element id; positional
 * "over target N" feedback is limited because dnd-kit's `over` is null under
 * custom collision.
 */

import {
  KeyboardCode,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type {
  Announcements,
  KeyboardCoordinateGetter,
  ScreenReaderInstructions,
} from "@dnd-kit/core";
import { useMemo } from "react";

/** One grid-cell pitch in pixels: horizontal = column + gap, vertical = row + gap. */
export interface CanvasKeyboardStep {
  x: number;
  y: number;
}

/**
 * Fallback pitch used when the component has not yet measured the grid (e.g. the
 * first render before layout). 50px is a reasonable single-cell nudge; the
 * component supplies the real pitch as soon as it knows the grid geometry.
 */
export const DEFAULT_CANVAS_KEYBOARD_STEP: CanvasKeyboardStep = { x: 50, y: 50 };

/**
 * Build a keyboard coordinate getter that moves the dragged element by one grid
 * cell pitch per arrow-key press. Other keys are ignored (return undefined), so
 * dnd-kit falls back to its default handling (Space/Enter to drop, Escape to
 * cancel).
 */
export function createCanvasKeyboardCoordinates(
  step: CanvasKeyboardStep = DEFAULT_CANVAS_KEYBOARD_STEP,
): KeyboardCoordinateGetter {
  const getter: KeyboardCoordinateGetter = (event, { currentCoordinates }) => {
    switch (event.code) {
      case KeyboardCode.Right:
        event.preventDefault();
        return { ...currentCoordinates, x: currentCoordinates.x + step.x };
      case KeyboardCode.Left:
        event.preventDefault();
        return { ...currentCoordinates, x: currentCoordinates.x - step.x };
      case KeyboardCode.Down:
        event.preventDefault();
        return { ...currentCoordinates, y: currentCoordinates.y + step.y };
      case KeyboardCode.Up:
        event.preventDefault();
        return { ...currentCoordinates, y: currentCoordinates.y - step.y };
      default:
        return undefined;
    }
  };
  return getter;
}

/**
 * Sensors for the canvas DndContext: the existing PointerSensor (unchanged 5px
 * activation distance) plus a KeyboardSensor using the grid-cell getter.
 * `step` should be the measured grid pitch; pass a stable object (memoize in the
 * component) so the getter is not rebuilt every render.
 */
export function useCanvasSensors(step?: CanvasKeyboardStep) {
  const stepX = step?.x;
  const stepY = step?.y;
  const coordinateGetter = useMemo(
    () =>
      createCanvasKeyboardCoordinates(
        stepX !== undefined && stepY !== undefined ? { x: stepX, y: stepY } : undefined,
      ),
    [stepX, stepY],
  );

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  });
  const keyboardSensor = useSensor(KeyboardSensor, { coordinateGetter });

  return useSensors(pointerSensor, keyboardSensor);
}

/**
 * Screen-reader instructions announced when a draggable canvas element receives
 * focus (spec section 9).
 */
export const canvasScreenReaderInstructions: ScreenReaderInstructions = {
  draggable:
    "To move a canvas element, press Space or Enter to pick it up. " +
    "While holding it, use the arrow keys to move it one grid cell at a time. " +
    "Press Space or Enter again to drop it in its new position, or press Escape to cancel.",
};

/**
 * Live-region announcements for the drag lifecycle (spec section 9). Keyed on
 * the active element id; see the KEYBOARD-GETTER NOTE for why positional
 * feedback via `over` is limited under custom collision.
 */
export const canvasDndAnnouncements: Announcements = {
  onDragStart({ active }) {
    return `Picked up canvas element ${active.id}.`;
  },
  onDragMove({ active, over }) {
    if (over) {
      return `Canvas element ${active.id} is over target ${over.id}.`;
    }
    return `Canvas element ${active.id} is moving.`;
  },
  onDragOver({ active, over }) {
    if (over) {
      return `Canvas element ${active.id} is over target ${over.id}.`;
    }
    return `Canvas element ${active.id} is no longer over a target.`;
  },
  onDragEnd({ active, over }) {
    if (over) {
      return `Canvas element ${active.id} was dropped on target ${over.id}.`;
    }
    return `Canvas element ${active.id} was dropped.`;
  },
  onDragCancel({ active }) {
    return `Moving canvas element ${active.id} was cancelled; it returned to its original position.`;
  },
};

/**
 * Convenience bundle for the DndContext `accessibility` prop:
 *   <DndContext accessibility={canvasDndAccessibility} ... />
 */
export const canvasDndAccessibility = {
  announcements: canvasDndAnnouncements,
  screenReaderInstructions: canvasScreenReaderInstructions,
};
